import React, { useRef } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { logger } from '@/lib/procurement/logger';

interface ProcurementErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

interface CircuitBreakerState {
  failures: number;
  lastFailureTime: number;
  state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
}

const ProcurementErrorFallback: React.FC<{ 
  onRetry?: () => void;
  error?: Error;
}> = ({ onRetry, error }) => {
  const circuitBreakerRef = useRef<CircuitBreakerState>({
    failures: 0,
    lastFailureTime: 0,
    state: 'CLOSED'
  });

  const handleRetry = () => {
    const breaker = circuitBreakerRef.current;
    const now = Date.now();

    // Circuit breaker logic: 5 failures in 60s -> OPEN, 30s reset to HALF_OPEN
    if (breaker.state === 'OPEN') {
      if (now - breaker.lastFailureTime > 30000) {
        breaker.state = 'HALF_OPEN';
        logger.info('ERROR_BOUNDARY', 'Circuit breaker HALF_OPEN for retry');
      } else {
        logger.info('ERROR_BOUNDARY', 'Circuit breaker OPEN, retry blocked');
        return;
      }
    }

    // Fail-fast for 403 AuthZ errors
    if (error?.message.includes('403') || error?.message.includes('Unauthorized')) {
      logger.error('ERROR_BOUNDARY', 'AuthZ error - fail fast', { error: error.message });
      window.location.href = '/login';
      return;
    }

    breaker.failures++;
    breaker.lastFailureTime = now;

    if (breaker.failures >= 5) {
      breaker.state = 'OPEN';
      logger.error('ERROR_BOUNDARY', 'Circuit breaker OPEN after max failures');
    }

    logger.info('ERROR_BOUNDARY', 'User retry attempt', { failures: breaker.failures });
    onRetry?.();
  };

  const isRetryBlocked = () => {
    const breaker = circuitBreakerRef.current;
    return breaker.state === 'OPEN' && 
           (Date.now() - breaker.lastFailureTime < 30000);
  };

  return (
    <Alert className="mx-auto max-w-md">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-3">
          <p>Có lỗi xảy ra với module Procurement. Vui lòng thử lại.</p>
          {error?.message.includes('403') && (
            <p className="text-sm text-destructive">
              Bạn không có quyền truy cập. Hệ thống sẽ chuyển về trang đăng nhập.
            </p>
          )}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRetry}
              disabled={isRetryBlocked()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {isRetryBlocked() ? 'Vui lòng chờ...' : 'Thử lại'}
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => window.location.reload()}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Tải lại trang
            </Button>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

export const ProcurementErrorBoundary: React.FC<ProcurementErrorBoundaryProps> = ({ 
  children, 
  onRetry 
}) => {
  return (
    <ErrorBoundary 
      fallback={<ProcurementErrorFallback onRetry={onRetry} />}
    >
      {children}
    </ErrorBoundary>
  );
};